import {
    matchRule,
    removeSpecialCharacters
} from "./TextUtilities.js";

// Safely merge two objects without risking source object changes.
export function safeMerge(a, b, conflictResolution = "b") {
    const _a = JSON.parse(JSON.stringify(a));
    const _b = JSON.parse(JSON.stringify(b));

    const result = {};

    for (let key in _a) {
        result[key] = _a[key];
    }

    for (let key in _b) {
        if (!result[key] || (result[key] && conflictResolution == "b"))
            result[key] = _b[key];
    }

    return result;
}

// TODO: Deprecate '...' spread notation handles this for us.
export function mergeLeft(a, b) {
    for (var key in b) {
        if (typeof b[key] == "object" && b[key] !== null && typeof a[key] == "object" && a[key] != null)
            mergeLeft(a[key], b[key]);
        else a[key] = b[key];
    }

    return a;
}

export function recursiveSearch({
    data,
    searchable = [],
    query,
    mode = "contains",
    smartSearch = true,
    caseSensitive = false,
}) {
    const isQueryMatch = (keyConfig, val) => {
        if (!query || query.replace(" ", "") == "") return true;

        let _query = caseSensitive ? query + "" : query.toLowerCase();
        switch (mode) {
            case "exact": {
                if (caseSensitive) {
                    if (val == _query) return true;
                    if (val && smartSearch && removeSpecialCharacters(val) == removeSpecialCharacters(_query))
                        return true;
                }
                else {
                    if (val && val.toLowerCase && val.toLowerCase() == _query.toLowerCase())
                        return true;
                    if (val && val.toLowerCase && smartSearch && removeSpecialCharacters(val.toLowerCase()) == removeSpecialCharacters(_query).toLowerCase())
                        return true;
                }
                break;
            }
            default: {
                if (caseSensitive) {
                    if (val && val.indexOf(_query) > -1) return true;
                    if (val && smartSearch && removeSpecialCharacters(val).indexOf(removeSpecialCharacters(_query)) > -1)
                        return true;
                }
                else {
                    if (val && val.toLowerCase && val.toLowerCase().indexOf(_query.toLowerCase()) > -1)
                        return true;
                    if (val && val.toLowerCase && smartSearch && removeSpecialCharacters(val.toLowerCase()).indexOf(removeSpecialCharacters(_query.toLowerCase())) > -1)
                        return true;
                }
                break;
            }
        }

        return false;
    };

    const search = (parentKey, obj) => {
        // Break pointer refs to keep object mutations safe,
        // specifically the array filtering in the last 'if' block
        let _data = JSON.parse(JSON.stringify(obj));
        let qualified = false;

        for (var key in _data) {
            let pathKey = parentKey ? `${parentKey}.${key}` : key;
            let keyConfig = getKeyConfig(pathKey, searchable);

            if (searchable.length == 0 || keyConfig) {
                if (isQueryMatch(keyConfig, _data[key])) {
                    qualified = true;
                }
                else if (typeof _data[key] == "object" && _data[key] !== null) {
                    var result = search(pathKey, _data[key]);
                    if (results.qualified) {
                        _data[key] = result.data;
                        qualified = true;
                    }
                }
                else if (Array.isArray(data[key])) {
                    _data[key] = _data[key].filter(x => search(pathKey, x));
                    if (_data[key].length > 0) qualified = true;
                }
            }
        }

        return {
            qualified,
            data: _data,
        };
    };

    const results = [];
    for (let i = 0; i < data.length; i++) {
        var result = search(null, data[i]);
        if (result.qualified) results.push(result.data);
    }

    return results;

    function getKeyConfig(key, searchable) {
        for (let i = 0; i < searchable.length; i++) {
            if (typeof searchable[i] == "object" && searchable[i] !== null) {
                if (matchRule(key, searchable[i].key) || new RegExp(searchable[i].key).test(key)) {
                    return searchable[i];
                }
            }

            if (matchRule(key, searchable[i]) || new RegExp(searchable[i]).test(key)) {
                return {
                    key,
                };
            }
        }

        return null;
    }
}

export function sort(data, config = {
    direction: "asc",
    key: null,
    transformation: null,
    thenBy: []
}) {
    if (!config.direction) config.direction = "asc";
    if (!Array.isArray(config.thenBy)) config.thenBy = [];
    return data.slice().sort((a, b) => {
        let result;
        if (config.key) {
            result = sortTargets(
                getTarget(a, config.key, config.transformation),
                getTarget(b, config.key, config.transformation),
                config.direction
            );
        }
        else
            result = sortTargets(a, b, config.direction);

        if (result) return result;

        for (let i = 0; i < config.thenBy.length; i++) {
            if (config.thenBy.key) {
                result = sortTargets(
                    getTarget(a, config.thenBy[i].key, config.thenBy[i].transformation),
                    getTarget(b, config.thenBy[i].key, config.thenBy[i].transformation),
                    config.thenBy[i].direction ? config.thenBy[i].direction : config.direction
                );

                if (result) return result;
            }
        }

        return result;

        function sortTargets(targetA, targetB, direction) {
            let mode = direction.toLowerCase().indexOf("desc") > -1 ? "desc" : "asc";
            if (isNaN(targetA) || isNaN(targetB)) {
                if (targetA.toLowerCase && targetB.toLowerCase) {
                    if (targetA.toLowerCase() > targetB.toLowerCase()) return mode === "asc" ? 1 : -1;
                    else if (targetB.toLowerCase() > targetA.toLowerCase()) return mode === "asc" ? -1 : 1;
                }
                else {
                    if (targetA > targetB) return mode === "asc" ? 1 : -1;
                    else if (targetB > targetA) return mode === "asc" ? -1 : 1;
                }
            }
            else {
                return mode === "asc" ? targetA - targetB : targetB - targetA;
            }
        }

        function getTarget(data, key, transformation = null) {
            let target = data;
            let parts = key.split(".");

            for (let i = 0; i < parts.length; i++) {
                if (!target) return null;
                target = target[parts[i]];
            }

            if (transformation && typeof transformation === "function") {
                target = transformation(target);
            }

            return target;
        }
    });
}

export function generateFileFormData(root, inputs = []) {
    let FD = new FormData();
    let data = root;

    for (let key in data)
        FD.append(key, data[key]);

    for (let i = 0; i < inputs.length; i++) {
        if (inputs[i].files) {
            for (let i = 0; i < inputs[i].files.length; i++)
                FD.append(inputs[i].name, inputs[i].files[i]);
        }
    }

    return FD;
}